import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Checkbox } from 'primereact/checkbox';
import { SelectButton } from 'primereact/selectbutton';
import { Message } from 'primereact/message';
import { TabView, TabPanel } from 'primereact/tabview';
import DealerDropdown from '../../../../../components/common/DealerDropdown';
import DealerAddressDropdown from '../../../../../components/common/DealerAddressDropdown';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { useUserAccessForm } from '../hooks/useUserAccessForm';
import { USER_TYPES } from '../../constants';
import ModuleRoleSelector from './ModuleRoleSelector';
import EffectivePermissionsPanel from './EffectivePermissionsPanel';

const UserAccessDialog = ({ visible, user, saving, onHide, onSubmit }) => {
    const { th } = useLanguage();
    const form = useUserAccessForm({ visible, user });
    const { account, changeAccount, errors, isEditMode } = form;

    const userTypeOptions = [
        { value: USER_TYPES.KARSAN, label: th('authorizationV2.userTypes.karsan') },
        { value: USER_TYPES.DEALER, label: th('authorizationV2.userTypes.dealer') }
    ];

    const submit = () => {
        if (!form.validate()) return;
        onSubmit({ ...form.buildPayload(), userId: form.userId, isEditMode });
    };

    const fieldError = (field) => errors[field] && <small className="p-error">{errors[field]}</small>;

    const textField = (field, label, props = {}) => (
        <div className="field col-12 md:col-4">
            <label htmlFor={`ua-${field}`} className="font-bold">
                {label}
                {!isEditMode && '*'}
            </label>
            <InputText id={`ua-${field}`} value={account[field]} onChange={(e) => changeAccount(field, e.target.value)} disabled={isEditMode} className={errors[field] ? 'p-invalid' : ''} {...props} />
            {fieldError(field)}
        </div>
    );

    const footer = (
        <div>
            <Button type="button" label={th('common.cancel')} icon="pi pi-times" outlined onClick={onHide} disabled={saving} />
            <Button type="button" label={th('common.save')} icon="pi pi-check" onClick={submit} loading={saving} disabled={form.rolesLoading} />
        </div>
    );

    const roleSection = (
        <>
            <ModuleRoleSelector form={form} />
            {!isEditMode && form.userType === USER_TYPES.KARSAN && <Message severity="warn" className="w-full justify-content-start mt-3" text={th('authorizationV2.user.karsanLegacyWarning')} />}
        </>
    );

    return (
        <Dialog
            header={isEditMode ? th('authorizationV2.user.editTitle', { name: `${account.firstName} ${account.lastName}` }) : th('authorizationV2.user.newUser')}
            visible={visible}
            onHide={onHide}
            footer={footer}
            style={{ width: '75rem' }}
            breakpoints={{ '1200px': '90vw', '768px': '100vw' }}
            modal
            maximizable
            className="authz-v2"
        >
            <h4 className="authz-v2-section-title">{th('authorizationV2.user.userInfo')}</h4>
            <div className="grid formgrid p-fluid">
                {textField('firstName', th('authorizationV2.user.firstName'))}
                {textField('lastName', th('authorizationV2.user.lastName'))}
                {textField('email', th('authorizationV2.user.email'), { type: 'email', keyfilter: /[^\s]/ })}
                <div className="field col-12 md:col-4">
                    <label className="font-bold">{th('authorizationV2.user.userType')}*</label>
                    <SelectButton value={form.userType} options={userTypeOptions} onChange={(e) => e.value && form.setUserType(e.value)} allowEmpty={false} />
                </div>
                <div className="field col-12 md:col-4">
                    <label htmlFor="ua-phoneNumber" className="font-bold">
                        {th('authorizationV2.user.phone')}
                        {!isEditMode && '*'}
                    </label>
                    <div className="p-inputgroup">
                        <span className="p-inputgroup-addon">+90</span>
                        <InputText
                            id="ua-phoneNumber"
                            value={account.phoneNumber}
                            keyfilter="int"
                            maxLength={15}
                            onChange={(e) => changeAccount('phoneNumber', e.target.value)}
                            disabled={isEditMode}
                            className={errors.phoneNumber ? 'p-invalid' : ''}
                            placeholder="5XX XXX XX XX"
                        />
                    </div>
                    {fieldError('phoneNumber')}
                </div>
                {textField('identityNo', th('authorizationV2.user.identityNo'), { keyfilter: 'int', maxLength: 11 })}
                {!isEditMode && (
                    <>
                        <div className="field col-12 md:col-4">
                            <label htmlFor="ua-password" className="font-bold">
                                {th('authorizationV2.user.password')}*
                            </label>
                            <Password inputId="ua-password" value={account.password} onChange={(e) => changeAccount('password', e.target.value)} toggleMask feedback={false} className={errors.password ? 'p-invalid' : ''} />
                            {fieldError('password')}
                        </div>
                        <div className="field col-12 md:col-4">
                            <label htmlFor="ua-passwordConfirmed" className="font-bold">
                                {th('authorizationV2.user.passwordConfirm')}*
                            </label>
                            <Password
                                inputId="ua-passwordConfirmed"
                                value={account.passwordConfirmed}
                                onChange={(e) => changeAccount('passwordConfirmed', e.target.value)}
                                toggleMask
                                feedback={false}
                                className={errors.passwordConfirmed ? 'p-invalid' : ''}
                            />
                            {fieldError('passwordConfirmed')}
                        </div>
                        <div className="field col-12 md:col-4 flex align-items-center gap-2 mt-4">
                            <Checkbox inputId="ua-isEmailEnabled" checked={account.isEmailEnabled} onChange={(e) => changeAccount('isEmailEnabled', e.checked)} />
                            <label htmlFor="ua-isEmailEnabled">{th('authorizationV2.user.sendEmail')}</label>
                        </div>
                    </>
                )}
            </div>

            {isEditMode ? (
                <TabView className="mt-2">
                    <TabPanel header={th('authorizationV2.user.rolesTab')} leftIcon="pi pi-users mr-2">
                        {roleSection}
                    </TabPanel>
                    <TabPanel header={th('authorizationV2.user.effectiveTab')} leftIcon="pi pi-shield mr-2">
                        <EffectivePermissionsPanel effective={form.effective} loading={form.effectiveLoading} />
                    </TabPanel>
                </TabView>
            ) : (
                <div className="mt-3">{roleSection}</div>
            )}

            <h4 className="authz-v2-section-title mt-4">{th('authorizationV2.user.dealerInfo')}</h4>
            <div className="grid formgrid p-fluid">
                <div className="field col-12 md:col-4">
                    <label className="font-bold">
                        {th('authorizationV2.user.dealer')}
                        {!isEditMode && '*'}
                    </label>
                    <DealerDropdown value={account.dealer} onChange={form.changeDealer} disabled={isEditMode} className={errors.dealer ? 'p-invalid w-full' : 'w-full'} placeholder={th('authorizationV2.user.dealerPlaceholder')} />
                    {fieldError('dealer')}
                </div>
                <div className="field col-12 md:col-4">
                    <label className="font-bold">
                        {th('authorizationV2.user.address')}
                        {!isEditMode && '*'}
                    </label>
                    <DealerAddressDropdown value={account.addrId} onChange={form.changeAddress} dealerId={account.dealer?.id} disabled={isEditMode || !account.dealer} placeholder={th('authorizationV2.user.addressPlaceholder')} />
                    {fieldError('addrId')}
                </div>
                <div className="field col-12 md:col-4">
                    <label className="font-bold">
                        <i className={`pi ${form.countryLoading ? 'pi-spin pi-spinner' : 'pi-flag'} mr-1`} />
                        {th('authorizationV2.user.country')}
                    </label>
                    <InputText value={account.countryName || account.country} disabled />
                    {fieldError('country')}
                </div>
            </div>
        </Dialog>
    );
};

export default UserAccessDialog;
